import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import { getAdminPromos, createAdminPromo, updateAdminPromo, deleteAdminPromo } from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Tag, Copy } from 'lucide-react';

const APPLIES_TO_LABELS = {
  all: 'Tous les achats',
  raffle: 'Tickets de tirage',
  pack: 'Recharges crédit',
  vip: 'Abonnements VIP',
};

const emptyForm = {
  code: '',
  discount_type: 'percent',  // 'percent' | 'amount' (UI only - mapped to fields)
  discount_pct: '0',
  discount_amount: '0',
  bonus_points: '0',
  max_uses: '0',
  max_uses_per_user: '1',
  min_amount: '0',
  applies_to: 'all',
  expires_at: '',
  description: '',
  enabled: true,
};

export default function AdminPromos() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPromo, setEditPromo] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    loadPromos();
  }, [user, isAdmin, navigate, authLoading]);

  const loadPromos = async () => {
    try {
      const res = await getAdminPromos();
      setPromos(res.data.promos || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditPromo(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditPromo(p);
    setForm({
      code: p.code,
      discount_type: (p.discount_amount && Number(p.discount_amount) > 0) ? 'amount' : 'percent',
      discount_pct: String(p.discount_pct || 0),
      discount_amount: String(p.discount_amount || 0),
      bonus_points: String(p.bonus_points || 0),
      max_uses: String(p.max_uses || 0),
      max_uses_per_user: String(p.max_uses_per_user ?? 1),
      min_amount: String(p.min_amount || 0),
      applies_to: p.applies_to || 'all',
      expires_at: p.expires_at ? String(p.expires_at).slice(0, 10) : '',
      description: p.description || '',
      enabled: p.enabled,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.code.trim()) { toast.error('Code requis'); return; }
    const payload = {
      code: form.code,
      discount_pct: form.discount_type === 'percent' ? parseInt(form.discount_pct) || 0 : 0,
      discount_amount: form.discount_type === 'amount' ? parseFloat(form.discount_amount) || 0 : 0,
      bonus_points: parseInt(form.bonus_points) || 0,
      max_uses: parseInt(form.max_uses) || 0,
      max_uses_per_user: parseInt(form.max_uses_per_user) || 0,
      min_amount: parseFloat(form.min_amount) || 0,
      applies_to: form.applies_to,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      description: form.description,
      enabled: form.enabled,
    };
    try {
      if (editPromo) {
        await updateAdminPromo(editPromo._id, payload);
        toast.success('Code mis à jour');
      } else {
        await createAdminPromo(payload);
        toast.success('Code créé');
      }
      setDialogOpen(false);
      loadPromos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce code promo ?')) return;
    try {
      await deleteAdminPromo(id);
      toast.success('Supprimé');
      loadPromos();
    } catch { toast.error('Erreur'); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`Code "${code}" copié`);
  };

  if (!user || !isAdmin) return null;

  return (
    <div className="flex min-h-screen bg-cream">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold text-rolex-green flex items-center gap-3">
              <Tag className="w-8 h-8 text-gold-dark" />
              Codes Promo
            </h1>
            <p className="text-rolex-green/65 mt-1 text-sm">
              Créez des codes utilisables au moment du paiement. Pourcentage de remise ou montant fixe en £.
            </p>
          </div>
          <Button className="bg-gold-gradient text-rolex-green gap-2 rounded-xl font-bold" onClick={openCreate}>
            <Plus className="w-4 h-4" /> Nouveau code
          </Button>
        </div>

        <Card className="bg-white border-2 border-gold/20">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Réduction</TableHead>
                  <TableHead>Bonus crédit</TableHead>
                  <TableHead>Applicable à</TableHead>
                  <TableHead>Utilisations</TableHead>
                  <TableHead>Min. achat</TableHead>
                  <TableHead>Expire</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {promos.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="py-12 text-center text-rolex-green/55">
                    <Tag className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p>Aucun code promo</p>
                  </TableCell></TableRow>
                ) : promos.map((p) => (
                  <TableRow key={p._id}>
                    <TableCell>
                      <button onClick={() => copyCode(p.code)} className="flex items-center gap-2 font-mono font-bold text-rolex-green hover:text-gold-dark" title="Cliquer pour copier">
                        {p.code} <Copy className="w-3 h-3 opacity-50" />
                      </button>
                      {p.description && <p className="text-[10px] text-rolex-green/50 mt-1">{p.description}</p>}
                    </TableCell>
                    <TableCell>
                      {p.discount_pct > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">-{p.discount_pct}%</Badge>}
                      {p.discount_amount > 0 && <Badge className="bg-blue-100 text-blue-700 border-blue-200 ml-1">-£{p.discount_amount}</Badge>}
                      {!p.discount_pct && !p.discount_amount && '-'}
                    </TableCell>
                    <TableCell>{p.bonus_points > 0 ? <Badge className="bg-gold/20 text-gold-dark">+£{p.bonus_points}</Badge> : '-'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{APPLIES_TO_LABELS[p.applies_to] || p.applies_to}</Badge></TableCell>
                    <TableCell className="font-mono text-sm">
                      {p.used_count || 0}{p.max_uses > 0 ? ` / ${p.max_uses}` : ' / ∞'}
                      <span className="text-[10px] text-rolex-green/55 block">{p.max_uses_per_user > 0 ? `(${p.max_uses_per_user}/user)` : '(illimité/user)'}</span>
                    </TableCell>
                    <TableCell>{p.min_amount > 0 ? `£${p.min_amount}` : '-'}</TableCell>
                    <TableCell>
                      {p.expires_at ? (
                        <span className={`text-xs ${new Date(p.expires_at) < new Date() ? 'text-red-500' : 'text-rolex-green'}`}>
                          {new Date(p.expires_at).toLocaleDateString('fr-FR')}
                        </span>
                      ) : <span className="text-rolex-green/40 text-xs">Jamais</span>}
                    </TableCell>
                    <TableCell>
                      <Badge className={p.enabled ? 'bg-emerald-500 text-white' : 'bg-stone-300 text-stone-700'}>
                        {p.enabled ? 'Actif' : 'Inactif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDelete(p._id)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Create/Edit dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl bg-cream max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                {editPromo ? 'Modifier le code promo' : 'Nouveau code promo'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Code (ex: BIENVENUE10, NOEL2026)</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s/g, '') })}
                  className="rounded-xl font-mono uppercase font-bold"
                  placeholder="BIENVENUE10"
                />
              </div>

              <div className="space-y-2">
                <Label>Description (optionnel)</Label>
                <Input
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="rounded-xl"
                  placeholder="Ex: Code de bienvenue pour les nouveaux clients"
                />
              </div>

              {/* Type of discount - radio */}
              <div className="space-y-2">
                <Label>Type de réduction</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, discount_type: 'percent' })}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${form.discount_type === 'percent' ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 hover:border-emerald-300'}`}
                  >
                    <p className="font-bold text-sm">💯 Pourcentage</p>
                    <p className="text-xs text-rolex-green/65 mt-1">Ex: -10% sur le total</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, discount_type: 'amount' })}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${form.discount_type === 'amount' ? 'border-blue-500 bg-blue-50' : 'border-stone-200 hover:border-blue-300'}`}
                  >
                    <p className="font-bold text-sm">💷 Montant fixe</p>
                    <p className="text-xs text-rolex-green/65 mt-1">Ex: -£5 sur le total</p>
                  </button>
                </div>
              </div>

              {form.discount_type === 'percent' ? (
                <div className="space-y-2">
                  <Label>Pourcentage de réduction (1-100)</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={form.discount_pct}
                      onChange={(e) => setForm({ ...form, discount_pct: e.target.value })}
                      className="rounded-xl text-2xl font-mono font-bold text-center"
                    />
                    <span className="text-2xl font-bold text-emerald-700">%</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Montant de réduction en £</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-blue-700">£</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.discount_amount}
                      onChange={(e) => setForm({ ...form, discount_amount: e.target.value })}
                      className="rounded-xl text-2xl font-mono font-bold text-center"
                    />
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Achat minimum (£)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.min_amount}
                    onChange={(e) => setForm({ ...form, min_amount: e.target.value })}
                    className="rounded-xl"
                    placeholder="0 = aucun"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Applicable à</Label>
                  <select
                    value={form.applies_to}
                    onChange={(e) => setForm({ ...form, applies_to: e.target.value })}
                    className="w-full p-2 border border-gold/30 rounded-xl bg-white"
                  >
                    <option value="all">Tous les achats</option>
                    <option value="raffle">Tickets de tirage uniquement</option>
                    <option value="pack">Recharges crédit uniquement</option>
                    <option value="vip">Abonnements VIP uniquement</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Utilisations max au total (0 = illimité)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Utilisations max par client (0 = illimité)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.max_uses_per_user}
                    onChange={(e) => setForm({ ...form, max_uses_per_user: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Date d'expiration (optionnel)</Label>
                <Input
                  type="date"
                  value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className="rounded-xl"
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl bg-cream border border-gold/20">
                <Label className="font-bold">Code actif</Label>
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleSubmit} className="bg-gold-gradient text-rolex-green font-bold">
                {editPromo ? 'Mettre à jour' : 'Créer le code'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
